/**
 * 统一的API错误处理工具
 *
 * 提供标准化的错误处理方式，确保前后端错误响应格式的一致性
 */

import type { ApiResponse, StandardizedError } from '../types/api';
import { ERROR_CODE_MESSAGES } from '../types/api';

// 重新导出类型，方便使用
export type { StandardizedError };

/**
 * 从API响应中提取标准化的错误信息
 *
 * @param response - API响应对象
 * @param fallbackMessage - 默认错误消息
 * @returns 标准化的错误信息
 */
export function extractErrorFromResponse(
  response: ApiResponse,
  fallbackMessage: string = '请求失败'
): StandardizedError {
  // 如果响应成功，不应该调用此函数
  if (response.success) {
    throw new Error('extractErrorFromResponse should only be called for failed responses');
  }

  const errorCode = response.errorCode || 'UNKNOWN_ERROR';
  const errorMessage = response.error || ERROR_CODE_MESSAGES[errorCode] || fallbackMessage;

  return {
    message: errorMessage,
    code: errorCode,
    originalError: response
  };
}

/**
 * 从网络错误中提取标准化的错误信息
 *
 * @param error - 原始错误对象
 * @returns 标准化的错误信息
 */
export function extractErrorFromNetworkError(error: any): StandardizedError {
  // 网络错误（如 fetch 失败）
  if (!navigator.onLine) {
    return {
      message: '网络连接已断开，请检查网络设置',
      code: 'NETWORK_ERROR',
      originalError: error
    };
  }

  // HTTP 错误
  if (error instanceof Response) {
    const statusCode = error.status;

    switch (statusCode) {
      case 400:
        return {
          message: '请求参数错误',
          code: 'VALIDATION_ERROR',
          statusCode,
          originalError: error
        };
      case 401:
        return {
          message: '未授权访问，请重新登录',
          code: 'UNAUTHORIZED',
          statusCode,
          originalError: error
        };
      case 403:
        return {
          message: '权限不足',
          code: 'FORBIDDEN',
          statusCode,
          originalError: error
        };
      case 404:
        return {
          message: '请求的资源不存在',
          code: 'VIDEO_NOT_FOUND',
          statusCode,
          originalError: error
        };
      case 413:
        return {
          message: '上传文件过大',
          code: 'FILE_TOO_LARGE',
          statusCode,
          originalError: error
        };
      case 429:
        return {
          message: '请求过于频繁，请稍后再试',
          code: 'RATE_LIMIT_ERROR',
          statusCode,
          originalError: error
        };
      case 500:
        return {
          message: '服务器内部错误，请稍后重试',
          code: 'INTERNAL_ERROR',
          statusCode,
          originalError: error
        };
      default:
        return {
          message: `请求失败 (${statusCode})`,
          code: 'UNKNOWN_ERROR',
          statusCode,
          originalError: error
        };
    }
  }

  // 超时错误
  if (error.name === 'AbortError' || error.message?.includes('timeout')) {
    return {
      message: '请求超时，请检查网络连接',
      code: 'TIMEOUT_ERROR',
      originalError: error
    };
  }

  // JSON 解析错误
  if (error instanceof SyntaxError && error.message.includes('JSON')) {
    return {
      message: '服务器响应格式错误',
      code: 'PARSE_ERROR',
      originalError: error
    };
  }

  // 其他错误
  return {
    message: error.message || '未知错误',
    code: 'UNKNOWN_ERROR',
    originalError: error
  };
}

/**
 * 处理API响应的统一函数
 *
 * @param response - fetch Response 对象
 * @returns Promise<T> - 如果成功返回数据，否则抛出 StandardizedError
 * @throws StandardizedError
 */
export async function handleApiResponse<T>(response: Response): Promise<T> {
  let responseData: ApiResponse<T>;

  try {
    // 检查响应是否为JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // 非JSON响应，可能是服务器错误
      const text = await response.text();
      throw new Error(`服务器返回了非JSON响应: ${text.substring(0, 200)}`);
    }

    responseData = await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw extractErrorFromNetworkError(error);
    }
    throw extractErrorFromNetworkError(new Error('解析响应失败'));
  }

  if (!responseData.success) {
    // API业务逻辑错误
    throw extractErrorFromResponse(responseData, '操作失败');
  }

  if (responseData.data === undefined) {
    throw extractErrorFromNetworkError(new Error('响应数据为空'));
  }

  return responseData.data;
}

/**
 * 统一的fetch包装器，包含错误处理
 *
 * @param url - 请求URL
 * @param options - fetch选项
 * @returns Promise<T>
 * @throws StandardizedError
 */
export async function apiFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw response;
    }

    return await handleApiResponse<T>(response);
  } catch (error) {
    if (error instanceof Response) {
      throw extractErrorFromNetworkError(error);
    }

    throw error;
  }
}

/**
 * 获取用户友好的错误消息
 *
 * @param error - 错误对象（可以是 StandardizedError 或普通 Error）
 * @returns 用户友好的错误消息
 */
export function getErrorMessage(error: any): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return '未知错误';
}
